import { WrappedFieldInputProps, WrappedFieldMetaProps, WrappedFieldProps } from 'redux-form';
import { ToolTip } from 'components/Shared/ToolTip';
import { RenderWrapperError, getShouldShowError } from './renderWrapperError/themes/BGH-RD3';
import styles from './renderWrapper.scss';
import {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes
} from 'react';

type RenderComponent<T> = (
  input: WrappedFieldInputProps,
  props: T,
  meta: WrappedFieldMetaProps
) => JSX.Element;

export type WrappedRenderProps = WrappedFieldProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onSelect'> &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'onSelect'> &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'onSelect'> & {
    label?: string;
    tooltip?: ReactNode;
    className?: string;
    hideError?: boolean;
    CustomErrorComponent?: () => JSX.Element;
  };

export function renderWrapper<T>(render: RenderComponent<T>) {
  return function wrapped(props: WrappedRenderProps & T) {
    const rest = cleanProps(props);

    if (props.hideError) {
      props.meta.invalid = false;
    }

    return (
      <div className={props.className}>
        {props.label && (
          <span>
            <label htmlFor={props.id || props.input.name}>{props.label}</label>
            {props.tooltip && <ToolTip className={styles.toolTip} text={props.tooltip} />}
          </span>
        )}
        {render(props.input, rest as T, props.meta)}
        {!props.hideError && !rest.CustomErrorComponent && <RenderWrapperError {...props.meta} />}
        {!props.hideError && getShouldShowError(props.meta) && rest.CustomErrorComponent && (
          <rest.CustomErrorComponent />
        )}
      </div>
    );
  };
}

function cleanProps<T>(props: Partial<WrappedRenderProps> & T): T {
  const rest = Object.assign({}, props);
  delete rest.input;
  delete rest.label;
  delete rest.tooltip;
  delete rest.meta;
  delete rest.className;
  delete rest.hideError;
  return rest;
}
